import classes from './PerformanceComponents.module.css'
import YearCard from '../../stories/YearCard/YearCard'
import clsx from 'clsx'
import { useMediaQuery } from '@mantine/hooks'
import {
  WidgetContextProvider,
  useWidgetContext,
} from '../../widgets/providers'
import { getMaxPeriod, useData } from '../../widgets/api'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useCompetitionStats } from '../api'
import dayjs from 'dayjs'
import WaterfallChart from '../../widgets/components/WaterfallChart'
import LineChart from '../../widgets/components/LineChart'
import BarChart from '../../widgets/components/BarChart'
import { useCompetitionId } from '../../common/hooks'
import { OptionKey } from '../../stories/ViewSelector/ViewSelector'

export default function PerformanceGraph({
  view,
  component,
}: {
  view: OptionKey
  component: string
}) {
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const ctx = useWidgetContext()
  const metricKey = ctx.metric || ''
  const subjectType = ctx.subject_type || 'overall'
  const subjectId = ctx.subject ?? 0

  // Get competition stats for total teams/players
  const competition_id = useCompetitionId()
  const { data: stats } = useCompetitionStats({ competition_id })

  // Fetch max year period
  const { data: maxYearData } = useSuspenseQuery({
    queryKey: ['max-year-period', subjectType],
    queryFn: () =>
      getMaxPeriod({ period_type: 'year', subject_type: subjectType }),
  })

  // Use max year or fallback to current year
  const yearPeriod = maxYearData.max_period
    ? dayjs(maxYearData.max_period).startOf('year').format('YYYY-MM-DD')
    : dayjs().startOf('year').format('YYYY-MM-DD')

  // Get the year for the title
  const yearTitle = `Year ${dayjs(yearPeriod).format('YYYY')}`

  // Fetch all metric data for the year
  const { data: yearMetricsData } = useData({
    period_type: 'year',
    period_range: false,
    periods: [yearPeriod],
    competition_id,
    subject_type: subjectType,
    subject_ids: [subjectId],
    metrics: [metricKey, metricKey, metricKey, metricKey, metricKey],
    components: ['actual', 'target', 'percent', 'percent', 'percent'],
    statistics: ['value', 'value', 'value', 'achieved_teams', 'achieved'],
  })

  const value = (c: string, s: string) =>
    yearMetricsData.find((d) => d.component === c && d.statistic === s)?.value

  // Extract values from the metrics data
  const actualValue = value('actual', 'value') || 0
  const targetValue = value('target', 'value') || 0
  const percentValue = value('percent', 'value') || 0
  const achievedTeamsCount = value('percent', 'achieved_teams') || 0
  const achievedPlayersCount = value('percent', 'achieved') || 0

  return (
    <div>
      <div
        className={clsx(classes.panel_container, {
          [classes.panel_container_mobile]: !isDesktop,
        })}
      >
        <div className={classes.current_year}>
          <YearCard
            title={yearTitle}
            target={targetValue}
            actual={actualValue}
            percent={percentValue}
            achievedTeams={`${achievedTeamsCount} / ${stats.num_teams}`}
            achieved={`${achievedPlayersCount} / ${stats.num_players}`}
          />
        </div>
        {/*
            <div className={classes.previous_year}>
              <YearCard
                title="Previous Year"
                target={143865808}
                actual={131010404}
                achived={91.1}
                team={'5 / 25'}
              />
            </div>
          */}
        <div className={classes.graph_container}>
          <WidgetContextProvider
            context={{ subject_type: subjectType, subject: subjectId }}
          >
            {view === 'waterfall' && <WaterfallChart id="analysis_waterfall" />}
            {view === 'bar' && (
              <WidgetContextProvider
                context={{ component, statistic: 'value' }}
              >
                <BarChart id="analysis_barchart" />
              </WidgetContextProvider>
            )}
            {view === 'line' && (
              <WidgetContextProvider
                context={{
                  component,
                  statistic: component === 'score' ? 'value_ytd' : 'value',
                }}
              >
                <LineChart id="analysis_linechart" />
              </WidgetContextProvider>
            )}
          </WidgetContextProvider>
        </div>
      </div>
    </div>
  )
}
